import { z } from 'zod';

const paginationSchema = z.object({
    offset: z.number(),
    totalCount: z.number(),
    limit: z.number(),
    lastPage: z.boolean(),
});

const addressSchema = z.object({
    firstAddressLine: z.string(),
    secondAddressLine: z.string().nullable().optional(),
    zip: z.string(),
    city: z.string(),
    countryCode: z.string(),
    addressee: z.string().nullable().optional(),
    instructions: z.string().nullable().optional(),
});

const shippingAddressRequestSchema = z.object({
    isCompanyAddress: z.boolean().optional(),
    companyName: z.string().nullable().optional(),
    firstAddressLine: z.string(),
    secondAddressLine: z.string().nullable().optional(),
    zip: z.string(),
    city: z.string(),
    countryCode: z.string(),
    addressee: z.string().nullable().optional(),
});

export const ShowVAT = z.enum(['minimal', 'complete']);

const eInvoicingSchema = z.object({
    scheme: z.string(),
    showVAT: ShowVAT.optional(),
    regex: z.string(),
});

const termsAndConditionsSchema = z.object({
    version: z.string(),
    englishUrl: z.string(),
    url: z.string().optional(),
});

export const AccountDto = z.object({
    permissions: z.array(z.string()),
    name: z.string(),
    vatNumber: z.string(),
    address: addressSchema,
    currency: z.string(),
    fleetMonitorURL: z.string(),
    isOnboarded: z.boolean().optional(),
    isOnboardAllowed: z.boolean().optional(),
    allowedCountries: z.array(z.string()),
    isRegistrationNumberRequired: z.boolean(),
    vatPatterns: z.record(z.string(), z.string()),
    language: z.string(),
    eInvoicingDetails: z.array(eInvoicingSchema).optional(),
    termsAndConditions: termsAndConditionsSchema.optional(),
});

export const CompanyDetails = z.object({
    organizationNumber: z.string().optional(),
    primaryVat: z.string().optional(),
    invoiceEmailAddress: z.string(),
    eInvoicingId: z.string().nullable().optional(),
});

export const ContactDetails = z.object({
    contactPhoneNumber: z.string(),
    contactEmail: z.string(),
    communicationLanguage: z.string().optional().nullable(),
});

export const CardCustomization = z.object({
    firstRow: z.string().nullable().optional(),
    secondRow: z.string().nullable().optional(),
    alias: z.string().nullable().optional(),
    productId: z.number(),
    supportLabel: z.string(),
    supportPhone: z.string(),
});

export const CardOrderDetails = z.object({
    shippingAddress: shippingAddressRequestSchema,
    cardDetails: z.array(CardCustomization),
});

export const OnboardingRequest = z.object({
    companyDetails: CompanyDetails,
    contactDetails: ContactDetails,
    cardOrderDetails: CardOrderDetails,
    termsVersion: z.string(),
});

export const CustomerContract = z.object({
    companyName: z.string(),
    ...CompanyDetails.shape,
    vatNumber: z.string(),
    ...addressSchema.shape,
    customerId: z.string(),
    entityId: z.number().optional(),
    companyUUID: z.string(),
    contactUUID: z.string(),
    username: z.string(),
    customerSequenceNumber: z.number(),
    contactPhoneNumber: z.string(),
    contactEmail: z.string(),
    currency: z.string().optional(),
    tariffId: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    createdBy: z.string().optional(),
    updatedBy: z.string().optional(),
    communicationLanguage: z.string(),
    eInvoicingId: z.string().optional(),
});

export const ContractUpdate = z.object({
    contactPhoneNumber: z.string(),
    contactEmailAddress: z.string().optional(),
    invoiceEmailAddress: z.string(),
    organizationNumber: z.string().optional(),
    communicationLanguage: z.string(),
    eInvoicingId: z.string().optional(),
});

export const ChargingSession = z.object({
    sessionId: z.string(),
    token: z.object({
        number: z.string(),
        serial: z.string(),
        alias: z.string().nullable().optional(),
    }),
    chargingPoint: z.object({
        evseId: z.string().nullable().optional(),
        name: z.string().nullable().optional(),
        countryCode: z.string().nullable().optional(),
        plugType: z.string().nullable().optional(),
        address: z.string().nullable().optional(),
        city: z.string().nullable().optional(),
        zip: z.string().nullable().optional(),
        fleetMonitorUrl: z.string().nullable().optional(),
        location: z
            .object({
                longitude: z.number(),
                latitude: z.number(),
            })
            .optional(),
        operatorName: z.string().nullable().optional(),
    }),
    totalCost: z.number().nullable().optional(),
    energy: z.number().nullable().optional(),
    duration: z.string().nullable().optional(),
    startedAt: z.string().nullable().optional(),
    endedAt: z.string().nullable().optional(),
    invoiceNumber: z.string().nullable().optional(),
    invoicedAt: z.string().nullable().optional(),
    components: z
        .object({
            costType: z.string(),
            unitPrice: z.number(),
            unitType: z.string(),
            count: z.number(),
            totalPrice: z.number(),
        })
        .optional(),
});

export const ChargingSessions = z.object({
    chargingSessions: z.array(ChargingSession),
    pagination: paginationSchema,
});

export const ChargingActivitySummary = z.object({
    chargingActivityDate: z.string(),
    numberOfChargingSessions: z.number(),
    totalChargingCost: z.number().nullable().optional(),
    totalEnergyCharged: z.number().nullable().optional(),
});

export const ChargingActivityClusteredSummary = z.object({
    chargingActivitySummaryList: z.array(ChargingActivitySummary),
    totalChargingCost: z.number().nullable().optional(),
    totalEnergyCharged: z.number().nullable().optional(),
    clusteredBy: z.enum(['DAY', 'MONTH']),
});

export const RfidCard = z.object({
    serial: z.string(),
    number: z.string(),
    isActive: z.boolean(),
    alias: z.string().nullable().optional(),
});

export const RfidCardsList = z.object({
    cards: z.array(RfidCard),
});

export const RfidCardUpdate = z.object({
    serial: z.string(),
    alias: z.string().nullable().optional(),
    isActive: z.boolean(),
    hasStatusChanged: z.boolean().optional(),
});

export const QuickFeedbackCreate = z.object({
    rating: z.enum(['GOOD', 'BAD', '1', '2', '3', '4', '5']),
    description: z.string().nullable().optional(),
    origin: z.enum(['ONBOARDING', 'OVERVIEW']),
});

export const GeneralFeedbackCreate = z.object({
    rating: z.number(),
    description: z.string().nullable().optional(),
    canContact: z.boolean(),
    contact: z.string().nullable().optional(),
});

export const Card = z.object({
    cardNumber: z.string(),
    firstRow: z.string().nullable().optional(),
    secondRow: z.string().nullable().optional(),
    productId: z.string(),
    supportLabel: z.string(),
    supportPhone: z.string(),
});

export const CardOrdersResponse = z.object({
    orderId: z.string(),
    address: addressSchema,
    cardStatus: z.enum(['SHIPPED', 'RECEIVED', 'PLACED']),
    shippingDate: z.string().optional(),
    cards: z.array(Card),
    orderDate: z.string(),
    user: z
        .object({
            firstName: z.string(),
            lastName: z.string(),
            email: z.string().optional(),
        })
        .optional(),
});

export const CardOrdersList = z.object({
    cardOrderHistory: z.array(CardOrdersResponse),
    historySummary: z.object({
        totalCardsInProduction: z.number(),
        totalCardsOrdered: z.number(),
        totalCardsShipped: z.number(),
        totalOrders: z.number(),
    }),
});

export const Invoice = z.object({
    invoiceNumber: z.string().nullable().optional(),
    totalCost: z.number().nullable().optional(),
    issueDate: z.string().nullable().optional(),
    dueDate: z.string().nullable().optional(),
    downloadLink: z.string().nullable().optional(),
});

export const Invoices = z.object({
    invoices: z.array(Invoice),
    pagination: paginationSchema,
});

// Type exports
export type AccountType = z.infer<typeof AccountDto>;
export type OnboardingRequestType = z.infer<typeof OnboardingRequest>;
export type CompanyDetailsType = z.infer<typeof CompanyDetails>;
export type ContactDetailsType = z.infer<typeof ContactDetails>;
export type CardOrderDetailsType = z.infer<typeof CardOrderDetails>;
export type ShippingAddressType = z.infer<typeof shippingAddressRequestSchema>;
export type CustomerContractType = z.infer<typeof CustomerContract>;
export type ContractUpdateType = z.infer<typeof ContractUpdate>;
export type ChargingSessionsType = z.infer<typeof ChargingSessions>;
export type ChargingSessionType = z.infer<typeof ChargingSession>;
export type ChargingActivitySummaryType = z.infer<typeof ChargingActivitySummary>;
export type ChargingActivityClusteredSummaryType = z.infer<typeof ChargingActivityClusteredSummary>;
export type RfidCardType = z.infer<typeof RfidCard>;
export type RfidCardUpdateType = z.infer<typeof RfidCardUpdate>;
export type CardOrdersResponseType = z.infer<typeof CardOrdersResponse>;
export type CardOrdersListType = z.infer<typeof CardOrdersList>;
export type CardType = z.infer<typeof Card>;
export type InvoicesType = z.infer<typeof Invoices>;
export type InvoiceType = z.infer<typeof Invoice>;
export type QuickFeedbackCreateType = z.infer<typeof QuickFeedbackCreate>;
export type GeneralFeedbackCreateType = z.infer<typeof GeneralFeedbackCreate>;
export type EInvoicingDetailType = z.infer<typeof eInvoicingSchema>;
